import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Guid } from 'guid-typescript';
import { Observable, OperatorFunction } from 'rxjs';
import { tap } from 'rxjs/operators';
import { WebApiService } from 'src/app/shared';
import { CsSetupSection } from '../charging-stations-section.model';
import { CsSetupSectionsStore } from '../state/cs-setup-sections.state';

@Injectable({ providedIn: 'root' })
export class CsSetupSectionsService extends WebApiService {
  private csSetupsUrl = 'cssetups';

  constructor(protected http: HttpClient, protected snackBar: MatSnackBar, private store: CsSetupSectionsStore) {
    super(http, snackBar);
  }

  public getAll(pojectId: Guid): Observable<CsSetupSection[]> {
    return this.http.get<CsSetupSection[]>(`${this.offersUrl}/${pojectId}/${this.csSetupsUrl}`, { responseType: 'json' })
      .pipe(
        this.handleApiRequest(),
        this.updateSectionsInStore(this.store));
  }

  public add(pojectId: Guid, csSetupSection: CsSetupSection): Observable<CsSetupSection> {
    return this.http.post<CsSetupSection>(`${this.offersUrl}/${pojectId}/${this.csSetupsUrl}`, csSetupSection, { responseType: 'json' })
        .pipe(
          this.handleApiRequest(),
          this.addNewSectionToStore(this.store));
  }

  public update(pojectId: Guid, csSetupSection: CsSetupSection): Observable<CsSetupSection> {
    return this.http
        .put<CsSetupSection>(
          `${this.offersUrl}/${pojectId}/${this.csSetupsUrl}/${csSetupSection.id}`, csSetupSection, { responseType: 'json' })
        .pipe(
          this.handleApiRequest(),
          this.updateExistingSectionInStore(this.store));
  }

  public remove(pojectId: Guid, csSetupSectionId: Guid): Observable<CsSetupSection> {
    return this.http
    .delete<CsSetupSection>(`${this.offersUrl}/${pojectId}/${this.csSetupsUrl}/${csSetupSectionId}`, { responseType: 'json' })
    .pipe(
      this.handleApiRequest(),
      this.removeSectionFromStore(this.store));
  }

  private updateSectionsInStore(store: CsSetupSectionsStore): OperatorFunction<CsSetupSection[], CsSetupSection[]> {
    return (source$: Observable<CsSetupSection[]>): Observable<CsSetupSection[]>  => {
      return source$.pipe(
        tap(csSetupSections =>
          store.update(state => {
            state.csSetupSections = [...csSetupSections];
        }))
      );
    };
  }

  private updateExistingSectionInStore(store: CsSetupSectionsStore): OperatorFunction<CsSetupSection, CsSetupSection> {
    return (source$: Observable<CsSetupSection>): Observable<CsSetupSection>  => {
      return source$.pipe(
        tap(csSetupSection =>
          store.update(state => {
            const index = state.csSetupSections.findIndex(ss => ss.id.equals(csSetupSection.id));
            state.csSetupSections[index] = { ...csSetupSection };
        }))
      );
    };
  }

  private addNewSectionToStore(store: CsSetupSectionsStore): OperatorFunction<CsSetupSection, CsSetupSection> {
    return (source$: Observable<CsSetupSection>): Observable<CsSetupSection>  => {
      return source$.pipe(
        tap(csSetupSection =>
          store.update(state => {
            state.csSetupSections.push(csSetupSection);
        }))
      );
    };
  }

  private removeSectionFromStore(store: CsSetupSectionsStore): OperatorFunction<CsSetupSection, CsSetupSection> {
    return (source$: Observable<CsSetupSection>): Observable<CsSetupSection>  => {
      return source$.pipe(
        tap(csSetupSection =>
          store.update(state => {
            const index = state.csSetupSections.findIndex(ss => ss.id.equals(csSetupSection.id));
            if (index !== -1) {
              state.csSetupSections.splice(index, 1);
            }
        }))
      );
    };
  }
}
